import React from "react";
import * as api from "../services/api";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import ButtonLoader from "./ButtonLoader";
import { useSnackbarMessage } from "./Snackbar";
import * as logging from "../logging";
import { useRevalidator } from "react-router";
import { errorsFromApi } from "../form";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import * as f from "../utils/formatter.js";
import { faFile } from "@fortawesome/pro-solid-svg-icons";
import { parseUtcDateTime } from "shared/src/date.mjs";

/**
 * @typedef FileRef
 * @property {string} objectid
 * @property {number} type
 * @property {number} file
 */

/**
 * @typedef FileExtension
 * @property {() => void} onload
 *
 * @typedef {import('../services/api').File & FileExtension} File
 *
 * @param {FileRef} file
 *
 * @returns {File}
 */
function useFile(file) {
  const [state, setState] = React.useState({ loading: true, file: null });
  React.useEffect(() => {
    (async function () {
      const f = await api.file(file);
      setState({ ...f, onload: () => URL.revokeObjectURL(f.url) });
    })();
  }, []);

  return state;
}

/**
 * @param {object} props
 * @param {File} props.file
 */
function Image({ file, client_id, fileInfo, fileCollection, readOnly }) {
  const [loading, setLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const setSnackbarMessage = useSnackbarMessage();
  const revalidator = useRevalidator();
  const { t } = useTranslation();

  const allow_export = fileCollection?.allow_export ?? false;

  return (
    <>
      {!readOnly && client_id != null && (
        <IconButton
          sx={{ position: "absolute", right: 0 }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={loading}
        >
          <MoreVert />
          {loading && <ButtonLoader />}
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async () => {
            try {
              setLoading(true);
              setAnchorEl(null);
              const result = await api.patchClient({
                id: client_id,
                profile_photo: fileInfo,
              });
              const errors = errorsFromApi({ t, result });
              if (errors.length === 0) {
                revalidator.revalidate();
                setSnackbarMessage({ content: "Profilová fotka nastavena." });
              } else {
                setSnackbarMessage({
                  content: "Nepovedlo se nastavit profilovou fotku.",
                  severity: "error",
                });
              }
            } catch (e) {
              logging.error(e);
              setSnackbarMessage({
                content: "Nepovedlo se nastavit profilovou fotku.",
                severity: "error",
              });
            } finally {
              setLoading(false);
            }
          }}
        >
          Nastavit jako profilovou fotku
        </MenuItem>
        {allow_export && (
          <ExportFileMenuItem
            setAnchorEl={setAnchorEl}
            setLoading={setLoading}
            fileInfo={fileInfo}
            client_id={client_id}
          />
        )}
      </Menu>
      <img
        style={{ width: "100%", height: "100%", objectFit: "scale-down" }}
        src={file.url}
        onLoad={file.onload}
      />
    </>
  );
}

function ExportFileMenuItem({ setAnchorEl, setLoading, fileInfo, client_id }) {
  const revalidator = useRevalidator();
  const setSnackbarMessage = useSnackbarMessage();
  const { t } = useTranslation();

  return (
    <MenuItem
      onClick={async () => {
        try {
          setLoading(true);
          setAnchorEl(null);
          const result = await api.exportFiles({
            client_id: client_id,
            files: [fileInfo],
          });
          const errors = errorsFromApi({ t, result });
          if (errors.length === 0) {
            revalidator.revalidate();
            setSnackbarMessage({ content: "Dokument poslán na export." });
          } else {
            setSnackbarMessage({
              content: "Nepovedlo se exportovat dokument.",
              severity: "error",
            });
          }
        } catch (e) {
          logging.error(e);
          setSnackbarMessage({
            content: "Nepovedlo se exportovat dokument.",
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      }}
    >
      Exportovat
    </MenuItem>
  );
}

/**
 * @param {object} props
 * @param {File} props.file
 */
function Iframe({ file }) {
  return (
    <iframe
      style={{ width: "100%", height: "100%" }}
      src={file.url}
      onLoad={file.onload}
    />
  );
}

/**
 * @param {object} props
 * @param {File} props.file
 */
function IframePreview({
  file,
  client_id,
  fileInfo,
  fileCollection,
  readOnly,
}) {
  const type = file.contentType?.split("/")?.[0];

  switch (type) {
    case "image":
      return (
        <Image
          file={file}
          client_id={client_id}
          fileInfo={fileInfo}
          fileCollection={fileCollection}
          readOnly={readOnly}
        />
      );
  }
  return (
    <Iframe
      file={file}
      client_id={client_id}
      fileInfo={fileInfo}
      fileCollection={fileCollection}
    />
  );
}

/**
 * @param {object} props
 * @param {FileRef} props.fileInfo
 * @param {string | null} props.client_id
 */
export function FilePreview({
  fileInfo,
  client_id,
  fullscreen,
  fileCollection,
  readOnly,
}) {
  const file = useFile(fileInfo);

  return (
    <Box
      sx={{
        ...(fullscreen
          ? { height: "100%" }
          : { width: "40rem", height: "40rem" }),
        position: "relative",
      }}
    >
      {file.loading ? (
        <CircularProgress />
      ) : (
        <IframePreview
          client_id={client_id}
          file={file}
          fileInfo={fileInfo}
          fileCollection={fileCollection}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {FileRef} props.fileInfo
 */
function FileDialog({ fileInfo, onClose, client_id, fileCollection }) {
  const file = useFile(fileInfo);

  const [loading, setLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const allow_export = fileCollection?.allow_export ?? false;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { height: "100%" } } }}
    >
      <DialogTitle
        sx={{
          padding: "2.4rem 2rem",
          margin: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {fileCollection && (
          <>
            ${fileCollection.class_name} ($
            {f.longDateTime(parseUtcDateTime(fileCollection.created_at))}), $
            {f.value(fileCollection.state_name)}
          </>
        )}
        <Box>
          {client_id != null && (
            <IconButton
              sx={{
                backgroundColor: "background.default",
                marginRight: "35px",
              }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={loading}
            >
              <MoreVert />
              {loading && <ButtonLoader />}
            </IconButton>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
          >
            {allow_export && (
              <ExportFileMenuItem
                setAnchorEl={setAnchorEl}
                setLoading={setLoading}
                fileInfo={fileInfo}
                client_id={client_id}
              />
            )}
          </Menu>
          <IconButton aria-label="close" onClick={() => onClose()}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <FilePreview
          file={file}
          fileInfo={fileInfo}
          client_id={client_id}
          fullscreen
          fileCollection={fileCollection}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * @param {object} props
 * @param {FileRef} props.fileInfo
 */
export function FileLink({
  fileInfo,
  details,
  client_id,
  fileCollection,
  readOnly,
}) {
  const [show, setShow] = React.useState(false);

  return (
    <>
      <Button
        disabled={readOnly}
        variant="outlined"
        onClick={() => setShow((prev) => !prev)}
        startIcon={<FontAwesomeIcon icon={faFile} />}
      >
        {details?.name}
      </Button>
      {show && (
        <FileDialog
          fileInfo={fileInfo}
          onClose={() => setShow(false)}
          client_id={client_id}
          fileCollection={fileCollection}
        />
      )}
    </>
  );
}
